import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "edit-employee-form" }
const _hoisted_2 = { class: "work-day-hours" }
const _hoisted_3 = { class: "work-day-hours-label" }
const _hoisted_4 = { class: "work-day-hours-inputs" }
const _hoisted_5 = ["onUpdate:modelValue"]
const _hoisted_6 = { class: "delivery-manager-section" }
const _hoisted_7 = { class: "delivery-manager-label" }
const _hoisted_8 = { class: "delivery-manager-add" }
const _hoisted_9 = {
  key: 0,
  class: "delivery-manager-list"
}
const _hoisted_10 = { class: "delivery-manager-list-label" }
const _hoisted_11 = { class: "project-name" }
const _hoisted_12 = ["disabled", "onClick"]
const _hoisted_13 = {
  key: 0,
  class: "loading apollo"
}

import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";

import Modal from "@/components/Modal.vue";
import FormItem from "@/components/form/FormItem.vue";
import BasicButton from "@/components/BasicButton.vue";
import ImageUploader from "@/components/ImageUploader.vue";

import { UPDATE_USER } from "@/graphql/user/mutations";
import { GET_DEFAULT_ROLES } from "@/graphql/defaultRole/queries";
import { GET_DETAILED_EMPLOYEES } from "@/graphql/user/queries";
import {
  GET_BASIC_EMPLOYEE_PROJECTS,
  GET_DELIVERY_MANAGER_PROJECTS,
} from "@/graphql/project/queries";
import {
  ADD_PROJECT_STAKEHOLDER,
  DELETE_PROJECT_STAKEHOLDER,
} from "@/graphql/project/mutations";

import enUK from "@/locales/en-UK.json";
import { User, DefaultRole } from "@/helpers/user/typesDefinition";

interface BasicProject {
  id: string;
  name: string;
  color: string | null;
}

interface DeliveryManagerProject {
  project: {
    id: string;
    name: string;
  };
  role: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditEmployees',
  props: {
  employee: {
    type: Object as () => User,
    required: true,
  },
},
  emits: ["close"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();

// Fetch roles for the dropdown
const { result: rolesResult } = useQuery(GET_DEFAULT_ROLES);

// Initialize form with the employee data
const editEmployeeFirstName = ref(props.employee.firstName);
const editEmployeeLastName = ref(props.employee.lastName);
const editEmployeeEmail = ref(props.employee.email);
const editEmployeeRoleId = ref(
  props.employee.defaultRole?.id || props.employee.defaultRoleId || "",
);
const editEmployeeAvatarUrl = ref(props.employee.avatarUrl || "");

// Initialize workDayHours from employee data or use defaults
const workDayHours = ref<number[]>(
  props.employee.workDayHours?.length
    ? [...props.employee.workDayHours]
    : [0, 8, 8, 8, 8, 8, 0],
);

const selectedRegion = ref<string>(props.employee.region || "BE");

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Delivery Manager section
const selectedProjectId = ref("");

// Fetch projects where employee has an active role (for dropdown)
const { result: employeeProjectsResult } = useQuery(
  GET_BASIC_EMPLOYEE_PROJECTS,
  () => ({
    userId: props.employee.id,
  }),
);

// Fetch projects where employee is delivery manager
const {
  result: deliveryManagerResult,
  refetch: refetchDeliveryManager,
  loading: loadingDeliveryManager,
} = useQuery(GET_DELIVERY_MANAGER_PROJECTS, () => ({
  userId: props.employee.id,
}));

// Compute role options for the dropdown
const roleOptions = computed(() => {
  if (!rolesResult.value?.defaultRoles?.data) return [];
  return rolesResult.value.defaultRoles.data.map((role: DefaultRole) => ({
    name: role.name,
    id: role.id,
  }));
});

// Compute employee projects for dropdown (exclude already assigned as delivery manager)
const employeeProjectOptions = computed(() => {
  const projects = (employeeProjectsResult.value?.basicEmployeeProjects ??
    []) as BasicProject[];
  const dmProjects = (deliveryManagerResult.value?.deliveryManagerProjects ??
    []) as DeliveryManagerProject[];
  const dmProjectIds = new Set(dmProjects.map((p) => p.project.id));

  return projects
    .filter((p) => !dmProjectIds.has(p.id))
    .map((p) => ({
      name: p.name,
      id: p.id,
    }));
});

// Compute current delivery manager projects
const deliveryManagerProjects = computed(() => {
  return deliveryManagerResult.value?.deliveryManagerProjects ?? [];
});

const {
  mutate: updateEmployee,
  onError: onUpdateEmployeeError,
  onDone: onUpdateEmployeeDone,
  loading: updatingEmployee,
} = useMutation(UPDATE_USER, {
  refetchQueries: [{ query: GET_DETAILED_EMPLOYEES }],
});

onUpdateEmployeeError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updateEmployee"));
});

onUpdateEmployeeDone(() => {
  toast.success(t("success.updateEmployee"));
  emit("close");
});

// Delivery Manager mutations
const {
  mutate: addDeliveryManager,
  onError: onAddDeliveryManagerError,
  onDone: onAddDeliveryManagerDone,
  loading: addingDeliveryManager,
} = useMutation(ADD_PROJECT_STAKEHOLDER);

onAddDeliveryManagerError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.addDeliveryManager"));
});

onAddDeliveryManagerDone(() => {
  toast.success(t("success.addDeliveryManager"));
  refetchDeliveryManager();
  selectedProjectId.value = "";
});

const {
  mutate: removeDeliveryManager,
  onError: onRemoveDeliveryManagerError,
  onDone: onRemoveDeliveryManagerDone,
  loading: removingDeliveryManager,
} = useMutation(DELETE_PROJECT_STAKEHOLDER);

onRemoveDeliveryManagerError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.removeDeliveryManager"));
});

onRemoveDeliveryManagerDone(() => {
  toast.success(t("success.removeDeliveryManager"));
  refetchDeliveryManager();
});

const isLoading = computed(
  () =>
    updatingEmployee.value ||
    addingDeliveryManager.value ||
    removingDeliveryManager.value ||
    loadingDeliveryManager.value,
);

async function handleUpdateEmployee(): Promise<void> {
  if (
    !editEmployeeFirstName.value ||
    !editEmployeeLastName.value ||
    !editEmployeeEmail.value
  ) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  await updateEmployee({
    id: props.employee.id,
    firstName: editEmployeeFirstName.value,
    lastName: editEmployeeLastName.value,
    email: editEmployeeEmail.value,
    defaultRoleId: editEmployeeRoleId.value || null,
    avatarUrl: editEmployeeAvatarUrl.value || null,
    workDayHours: workDayHours.value,
    region: selectedRegion.value || undefined,
  });
}

function handleAvatarChange(fileId: string): void {
  editEmployeeAvatarUrl.value = fileId;
}

function closeModal(): void {
  emit("close");
}

async function handleAddDeliveryManager(): Promise<void> {
  if (!selectedProjectId.value) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  await addDeliveryManager({
    projectId: selectedProjectId.value,
    userId: props.employee.id,
    role: "delivery_manager",
  });
}

async function handleRemoveDeliveryManager(projectId: string): Promise<void> {
  await removeDeliveryManager({
    projectId: projectId,
    userId: props.employee.id,
    role: "delivery_manager",
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, { onClose: closeModal }, {
    header: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("titles.employee.edit")), 1)
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(FormItem, {
          input: editEmployeeFirstName.value,
          "onUpdate:input": _cache[0] || (_cache[0] = ($event: any) => ((editEmployeeFirstName).value = $event)),
          label: _unref(t)('labels.firstName'),
          name: "firstName",
          "input-type": "basic"
        }, null, 8, ["input", "label"]),
        _createVNode(FormItem, {
          input: editEmployeeLastName.value,
          "onUpdate:input": _cache[1] || (_cache[1] = ($event: any) => ((editEmployeeLastName).value = $event)),
          label: _unref(t)('labels.lastName'),
          name: "lastName",
          "input-type": "basic"
        }, null, 8, ["input", "label"]),
        _createVNode(FormItem, {
          input: editEmployeeEmail.value,
          "onUpdate:input": _cache[2] || (_cache[2] = ($event: any) => ((editEmployeeEmail).value = $event)),
          label: _unref(t)('labels.email'),
          name: "email",
          "input-type": "basic"
        }, null, 8, ["input", "label"]),
        _createVNode(FormItem, {
          input: editEmployeeRoleId.value,
          "onUpdate:input": _cache[3] || (_cache[3] = ($event: any) => ((editEmployeeRoleId).value = $event)),
          label: _unref(t)('labels.role'),
          name: "role",
          "input-type": "select",
          options: roleOptions.value
        }, null, 8, ["input", "label", "options"]),
        _createVNode(ImageUploader, {
          "current-url": editEmployeeAvatarUrl.value,
          onChanged: handleAvatarChange
        }, null, 8, ["current-url"]),
        _createVNode(FormItem, {
          input: selectedRegion.value,
          "onUpdate:input": _cache[4] || (_cache[4] = ($event: any) => ((selectedRegion).value = $event)),
          label: _unref(t)('labels.region'),
          name: "region",
          "input-type": "select",
          options: [
            { name: 'BE', id: 'BE' },
            { name: 'FR', id: 'FR' },
          ]
        }, null, 8, ["input", "label"]),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)("labels.workDayHours")), 1),
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(DAY_LABELS, (day, index) => {
              return _createElementVNode("div", {
                key: index,
                class: "work-day-hour-input"
              }, [
                _createElementVNode("label", null, _toDisplayString(day), 1),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((workDayHours.value[index]) = $event),
                  type: "number",
                  min: "0",
                  max: "24",
                  step: "0.5"
                }, null, 8, _hoisted_5), [
                  [_vModelText, workDayHours.value[index]]
                ])
              ])
            }), 64))
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)("labels.assignAsDeliveryManager")), 1),
          _createElementVNode("div", _hoisted_8, [
            _createVNode(FormItem, {
              input: selectedProjectId.value,
              "onUpdate:input": _cache[5] || (_cache[5] = ($event: any) => ((selectedProjectId).value = $event)),
              label: _unref(t)('labels.selectProject'),
              name: "project",
              "input-type": "select",
              options: employeeProjectOptions.value
            }, null, 8, ["input", "label", "options"]),
            _createVNode(BasicButton, {
              class: "add-dm-button",
              disabled: !selectedProjectId.value || isLoading.value,
              onClick: handleAddDeliveryManager
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.add")), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          (deliveryManagerProjects.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(t)("labels.currentDeliveryManagerProjects")), 1),
                _createElementVNode("ul", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(deliveryManagerProjects.value, (dmProject) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: dmProject.project.id,
                      class: "delivery-manager-item"
                    }, [
                      _createElementVNode("span", _hoisted_11, _toDisplayString(dmProject.project.name), 1),
                      _createElementVNode("button", {
                        class: "remove-button",
                        disabled: isLoading.value,
                        onClick: ($event: any) => (handleRemoveDeliveryManager(dmProject.project.id))
                      }, " × ", 8, _hoisted_12)
                    ]))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(_unref(t)("loading")), 1))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(BasicButton, {
        class: "edit-button",
        type: "submit",
        disabled: isLoading.value,
        onClick: handleUpdateEmployee
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)("buttons.save")), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(BasicButton, {
        class: "cancel-button",
        onClick: closeModal
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)("buttons.cancel")), 1)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})